import algoliasearch from 'algoliasearch/lite';
import { sponsoredProducts as SponsoredProductsUtil } from 'utils';
import tracer from './tracer';
import nodeRequester from './algoliaAxiosRequester';

/**
 * Enrich the algolia results with a custom ranking algorithm to get relevant search results
 * ex : when a user searches for "robinet", they are actually looking for "mitigeur", so we push relevant products
 * at the top of the result page
 *
 * JIRA : REC-1820
 */

export const rerankProducts = ({ response, rerankingConfig, flags }) => {
    const {
        categoryId,
        listingType,
        sponsoredProductsAds = [],
        urlModelId,
        hasPromotedFilter = false,
    } = rerankingConfig;
    const { hits } = response.results[0];
    const {
        PRODUCTS_CARDS_POSITIONS_FEATURE_A,
        PRODUCTS_CARDS_POSITIONS_FEATURE_B,
    } = SponsoredProductsUtil;

    const hasSponsoredProductPositions = flags?.isEnabled(
        'relevance.sponsored.product.positions.2',
    );

    const productCardPositions = hasSponsoredProductPositions
        ? PRODUCTS_CARDS_POSITIONS_FEATURE_B
        : PRODUCTS_CARDS_POSITIONS_FEATURE_A;

    const RECOMMENDED_POSITION = hasSponsoredProductPositions ? 2 : 3;
    const productPositions = productCardPositions.concat();
    const productsToRerank = [];

    if (listingType === 'search') {
        // We need to decrease all positions by 1 because we don't have any promoted card
        productPositions.forEach((pos, i) => productPositions[i] - 1);
        productPositions.unshift(RECOMMENDED_POSITION);
        productPositions.pop();
    } else if (listingType === 'category') {
        // Extract specific category cards (topSales, recommended, google shopping )
        const topSaleProductIndex = hits.findIndex(
            product =>
                product.banner.categories &&
                product.banner.categories.includes(`topSales.${categoryId}`),
        );
        if (topSaleProductIndex > -1) {
            productsToRerank.push({
                position: 0,
                product: hits.splice(topSaleProductIndex, 1)[0],
            });
        }

        // gs stands for Google Shopping
        const gsProductIndex = hits.findIndex(
            product => product.model_id === parseInt(urlModelId, 10),
        );
        if (gsProductIndex > -1) {
            productsToRerank.push({
                position: 0,
                product: hits.splice(gsProductIndex, 1)[0],
            });
        }

        const recommendedProductIndex = hits.findIndex(
            product =>
                product.banner.categories &&
                (product.banner.categories.includes(
                    `recommended.${categoryId}`,
                ) ||
                    product.banner.categories.includes(
                        `productOfTheWeek.${categoryId}`,
                    )),
        );
        // No recommended product ? Yeah, we take the place !
        if (recommendedProductIndex === -1) {
            productPositions.unshift(
                hasPromotedFilter
                    ? RECOMMENDED_POSITION
                    : // We need to offset the recommended position
                      // as the topSale will take a full row
                      RECOMMENDED_POSITION + 1,
            );
            productPositions.pop();
        } else {
            productsToRerank.push({
                position: hasPromotedFilter
                    ? RECOMMENDED_POSITION
                    : RECOMMENDED_POSITION + 1,
                product: hits.splice(recommendedProductIndex, 1)[0],
            });
        }
    }
    // Extract sponsored products and their corresponding position
    sponsoredProductsAds.forEach(({ product_id: productId }) => {
        const sponsoredProductIndex = hits.findIndex(
            // objectID is a string in Algolia ¯\_(ツ)_/¯
            ({ objectID }) => `${productId}` === objectID,
        );
        if (sponsoredProductIndex > -1) {
            const position = productPositions.shift();
            productsToRerank.push({
                position,
                product: hits.splice(sponsoredProductIndex, 1)[0],
            });
        }
    });

    productsToRerank.forEach(({ position, product }) =>
        hits.splice(position, 0, product),
    );
    return response;
};

export default tracer.wrap(
    'fragmentListing.createAlgoliaApp',
    function createAlgoliaApp({
        appId,
        searchApiKey,
        isSSR,
        rerankingConfig = {},
        flags = {},
    }) {
        const options = {};
        if (isSSR) {
            options.headers = {
                'Accept-Encoding': 'gzip',
                'User-Agent': 'ms-ssr-listing',
                Host: `${appId}-dsn.algolia.net`,
            };
            options.hosts = [
                {
                    url:
                        process.env.ENVOY_PROXY_HOST ||
                        `${appId}-dsn.algolia.net`,
                    accept: 1,
                    protocol: process.env.ENVOY_PROXY_SCHEME || 'http',
                },
            ];
            options.requester = nodeRequester();
        }

        const algoliaSearchClient = algoliasearch(appId, searchApiKey, options);

        const search = algoliaSearchClient.search.bind(algoliaSearchClient);
        algoliaSearchClient.search = (...params) =>
            search(...params).then(response =>
                rerankProducts({
                    response,
                    rerankingConfig,
                    flags,
                }),
            );

        return algoliaSearchClient;
    },
);
